import React, { useState, useCallback } from 'react';
import clsx from 'clsx';
import {
  UTM_SOURCE_KEY,
  PARTNER_ID_KEY,
  AUTH_NETWORKS,
  CONFIRM_TOKEN_ACTIONS,
  PASSWORD_RECOVERY_REDIRECT_KEY,
} from '../configs/constants';
import { injectedComponent, Text, useText } from '@base/core';
import { useResponseErrorMessage, useLocaleSwitchApp } from '@base/hooks/i18n';
import { useSessionStorage } from '@base/hooks/useSessionStorage';

export default injectedComponent(
  {
    name: 'LoginDialog',
    services: ['api', 'access', 'dialog', 'notifier'],
    components: ['AuthDialogBase', 'SessionKeyDialog'],
    styles: ['dialog', 'button', 'self'],
  },
  function LoginDialog({
                         services: { api, access, dialog, notifier },
                         components: { AuthDialogBase, SessionKeyDialog },
                         styles: { dialog: dialogStyles, self: selfStyles },
                         diSettings,
                       }) {
    const [login, setLogin] = useState('');
    const [password, setPassword] = useState('');
    const [updating, setUpdating] = useState(false);
    const [authError, setAuthError] = useState(null);
    const [validity, setValidity] = useState(false);
    const text = useText();
    const switchApp = useLocaleSwitchApp();
    const getErrorMessage = useResponseErrorMessage();
    const storage = useSessionStorage(login);
    const utm_source = sessionStorage.getItem(UTM_SOURCE_KEY) || '';
    const partnerId = sessionStorage.getItem(PARTNER_ID_KEY) || '';

    const persistState = useCallback((key, value) => {
      if (!storage) return;
      storage.set(key, value);
    }, [storage]);

    const handleLoginChange = (value) => {
      if (updating) return;
      setAuthError(null);
      setLogin(value);
    };

    const handlePasswordChange = (value) => {
      if (updating) return;
      setAuthError(null);
      setPassword(value);
    };

    const handleSignUpDialog = () => {
      if (diSettings.switchAppOnSignUp) {
        dialog.close();
        return switchApp(diSettings.switchAppOnSignUp, { dialog: 'signup' });
      }
      dialog.openById('signup');
    };

    const handlePasswordRecoveryDialog = () => {
      const dialogParam = login ? btoa(login) : null;

      if (diSettings.switchAppOnRecovery) {
        const searchParams = { dialog: 'password-recovery' };
        if (dialogParam) {
          searchParams['dialog-value'] = dialogParam;
        }

        try {
          sessionStorage.setItem(
            PASSWORD_RECOVERY_REDIRECT_KEY,
            JSON.stringify({ route: diSettings.switchAppOnRecovery, search: searchParams }),
          );
        } catch (e) {
        }
      }

      if (access.hasAnyAccess()) {
        access.logout();
      }

      if (diSettings.switchAppOnRecovery) {
        dialog.close();
        return;
      }

      dialog.openById('password-recovery', dialogParam);
    };

    const handleSNLogin = (sn, baseUrl) => {
      if (!AUTH_NETWORKS.includes(sn)) return;
      const searchParams = new URLSearchParams();
      if (utm_source) {
        searchParams.append('utm_source', utm_source);
      }
      if (partnerId) {
        searchParams.append('partner', partnerId);
      }
      const params = searchParams.size > 0 ? `?${searchParams.toString()}` : '';
      document.location.assign(`${baseUrl}/auth/${sn}${params}`);
    };

    const processResponseError = (response) => {
      if (response && response.result === 'error') {
        const responseMessage = response.message || '';
        const message = getErrorMessage(response);

        if (responseMessage === 'Session key request limit exceeded' || message === 'Session key request limit exceeded') {
          const errorData = {
            error: text('dialog.session_key.long_resend_limit_exceeded'),
            type: 'resend_limit',
          };

          let expiresAt = null;

          if (response.blockUntil || response.sessionKeyTTL) {
            const blockUntil = response.blockUntil || (response.sessionKeyTTL ? Math.floor(Date.now() / 1000) + response.sessionKeyTTL : null);
            if (blockUntil) {
              expiresAt = blockUntil;
              persistState('longResendTimer', { endTime: expiresAt });
            }
          }

          if (!expiresAt) {
            const savedLongResendTimer = storage?.get('longResendTimer');
            if (savedLongResendTimer && savedLongResendTimer.endTime) {
              expiresAt = savedLongResendTimer.endTime;
            }
          }

          if (expiresAt) {
            errorData.expiresAt = expiresAt;
          }

          persistState('error', errorData);
          setAuthError(text('dialog.session_key.long_resend_limit_exceeded'));
          notifier.push(text('dialog.session_key.long_resend_limit_exceeded'));
          return;
        }

        if (responseMessage.includes('INVALID-LOGIN-PASSWORD') || message.match(/^Member \d+ is not exist in panel \d+\.$/)) {
          return setAuthError(text('dialog.login.error_message__invalid_login_password'));
        }
        setAuthError(message);
      }
    };

    const handleLogin = async () => {
      if (updating || !validity) return;
      setUpdating(true);
      try {
        const response = await api.signIn({
          payload: { identity: 'email', login, password },
          responseType: 'full',
        });
        if (response && response.result === 'success') {
          notifier.closeAll();

          if (response.data.twoFactor === true) {
            if (response.data.sessionKeyTTL) {
              const endTimeSec = Math.floor(Date.now() / 1000) + response.data.sessionKeyTTL;
              persistState('longResendTimer', { endTime: endTimeSec });
            }

            const dialogParams = btoa(JSON.stringify({
              email: login,
              action: CONFIRM_TOKEN_ACTIONS.LOGIN,
              memberId: response.data.memberId,
              sessionKeyTTL: response.data.sessionKeyTTL,
              switchAppOnLogin: diSettings.switchAppOnLogin,
              onLogin: diSettings.onLogin,
            }));
            dialog.open(SessionKeyDialog, dialogParams);
            setUpdating(false);
          } else {
            if (storage) {
              storage.clearAppKeys();
            }

            const authToken = response.data.token;

            if (authToken) {
              access.login(authToken, {
                memberId: response.data.memberId || response.memberId,
                isLogged: true,
                fullMemberAccess: response.data.fullMemberAccess || false,
                viewCabinetMemberAccess: response.data.viewCabinetMemberAccess || false,
              });
            } else {
              access.login(response.data);
            }

            if (diSettings.switchAppOnLogin) {
              dialog.close();
              if (typeof diSettings.onLogin === 'function') {
                diSettings.onLogin();
              }
              switchApp(diSettings.switchAppOnLogin);
            }
          }
        } else {
          processResponseError(response);
        }
      } catch (e) {
        if (typeof e.json === 'function') {
          const response = await e.json();
          processResponseError(response);
        } else if (e.result === 'error' && e.message === 'Session key request limit exceeded') {
          const nowSec = Math.floor(Date.now() / 1000);
          const longResendData = storage?.get('longResendTimer');
          if (longResendData) {
            const remainingTime = longResendData.endTime - nowSec;
            if (remainingTime > 0) {
              persistState('longResendTimer', { endTime: longResendData.endTime });
            }
          }

          const localizedErrorMessage = text('dialog.session_key.long_resend_limit_exceeded');
          setAuthError(localizedErrorMessage);
          persistState('error', {
            error: localizedErrorMessage,
            type: 'resend_limit',
          });
          notifier.push(localizedErrorMessage);
        } else {
          setAuthError(text('dialog.login.error_message__unknown_error'));
        }
      } finally {
        setUpdating(false);
      }
    };

    const validateChild = (validationId, childValidity) => {
      setValidity(childValidity);
    };

    return (
      <AuthDialogBase
        login={login}
        onSNLogin={handleSNLogin}
        onLoginChange={handleLoginChange}
        password={password}
        onPasswordChange={handlePasswordChange}
        updating={updating}
        authError={authError}
        validity={validity}
        validate={validateChild}
        title={<Text>dialog.login.title</Text>}
        beforeActionButton={
          <span className={clsx(dialogStyles.regularText, selfStyles.regularText)}>
            <span className={dialogStyles.dialogLink} onClick={handlePasswordRecoveryDialog}>
              <Text>dialog.login.link__forgot_password_q</Text>
            </span>
          </span>
        }
        actionButtonText={<Text>dialog.login.button__login</Text>}
        onAction={handleLogin}
        afterActionButton={!access.hasAnyAccess() && (
          <div className={dialogStyles.regularText} style={{ textAlign: 'center', width: '100%' }}>
            <Text>dialog.login.text__dont_have_an_account_q</Text>
            <span className={dialogStyles.dialogLink} onClick={handleSignUpDialog}>
              <Text>dialog.login.link__create_account</Text>
            </span>
          </div>
        )}
      />
    );
  },
);
